<template>
  <v-form class="pa-6">
    <h3 class="text-left mb-1 text-primary">DigiLogix</h3>
    <p class="text-caption mb-4">
      Obtiene la credencial DigiLogix del seat configurado llamando a
      <code>POST /csc/v1/credentials/list-info</code> con el token USAGE.
    </p>

    <v-alert v-if="!hasUsageToken" type="warning" density="compact" variant="tonal" class="mb-4">
      No hay token USAGE configurado. Generá primero el token de Usage en
      <strong>Config Manager</strong> antes de continuar.
    </v-alert>

    <div class="d-flex ga-2 mb-6">
      <v-btn
        size="small"
        color="primary"
        :disabled="!hasUsageToken || loading"
        :loading="loading"
        @click="fetchListInfo">
        Obtener certificado DigiLogix
      </v-btn>
      <v-btn
        v-if="digilogixEntry"
        size="small"
        variant="tonal"
        @click="digilogixEntry = null; error = null">
        Limpiar
      </v-btn>
    </div>

    <v-alert v-if="error" type="error" density="compact" variant="tonal" class="mb-4">
      {{ error }}
    </v-alert>

    <template v-if="digilogixEntry">
      <v-divider class="mb-4" />
      <p class="text-caption text-medium-emphasis mb-3">
        Credencial <code>id: "digilogix"</code> encontrada en la respuesta de list-info:
      </p>
      <v-table density="compact" class="mb-4">
        <tbody>
          <tr>
            <td class="text-caption font-weight-medium" style="width:160px">Subject DN</td>
            <td class="text-caption font-mono">{{ digilogixEntry.cert?.subjectDN ?? '-' }}</td>
          </tr>
          <tr>
            <td class="text-caption font-weight-medium">Issuer DN</td>
            <td class="text-caption font-mono">{{ digilogixEntry.cert?.issuerDN ?? '-' }}</td>
          </tr>
          <tr>
            <td class="text-caption font-weight-medium">Serial Number</td>
            <td class="text-caption font-mono">{{ digilogixEntry.cert?.serialNumber ?? '-' }}</td>
          </tr>
          <tr>
            <td class="text-caption font-weight-medium">Válido desde</td>
            <td class="text-caption">{{ formatDate(digilogixEntry.cert?.validFrom) }}</td>
          </tr>
          <tr>
            <td class="text-caption font-weight-medium">Válido hasta</td>
            <td :class="['text-caption', isExpired(digilogixEntry.cert?.validTo) ? 'text-error' : '']">
              {{ formatDate(digilogixEntry.cert?.validTo) }}
              <v-chip v-if="isExpired(digilogixEntry.cert?.validTo)" color="error" size="x-small" class="ml-1">Expirado</v-chip>
            </td>
          </tr>
          <tr>
            <td class="text-caption font-weight-medium">Status</td>
            <td class="text-caption">{{ digilogixEntry.cert?.status ?? '-' }}</td>
          </tr>
          <tr>
            <td class="text-caption font-weight-medium">Multisign</td>
            <td class="text-caption">{{ digilogixEntry.multisign ?? '-' }}</td>
          </tr>
        </tbody>
      </v-table>

      <v-expansion-panels variant="accordion">
        <v-expansion-panel title="Respuesta completa (list-info)">
          <v-expansion-panel-text>
            <pre class="text-caption overflow-auto" style="max-height:300px">{{ JSON.stringify(rawResponse, null, 2) }}</pre>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </template>

    <template v-else-if="notFound">
      <v-alert type="info" density="compact" variant="tonal" class="mt-2">
        La respuesta de list-info no contiene ninguna entrada con
        <code>id: "digilogix"</code>. Verificá que el tenant tenga
        <strong>DigiLogix habilitado</strong> en el servidor.
      </v-alert>
    </template>

    <!-- SIGN SECTION -->
    <v-divider class="my-6" />
    <h4 class="text-subtitle-2 font-weight-medium mb-1">Firma DigiLogix</h4>
    <p class="text-caption text-medium-emphasis mb-4">
      Inicia una firma con hashes de prueba (SHA-256 aleatorios). Abre el portal de DigiLogix en una pestaña nueva y recupera los hashes firmados automáticamente al completar el flujo.
    </p>

    <div class="d-flex ga-4 align-center flex-wrap mb-3">
      <v-text-field
        v-model.number="hashCount"
        type="number"
        min="1"
        max="20"
        label="Cantidad de hashes"
        density="compact"
        variant="outlined"
        style="max-width:180px"
        hide-details />

      <v-radio-group v-model="authMode" inline density="compact" hide-details class="mt-0">
        <template #label>
          <span class="text-caption text-medium-emphasis mr-2">Autorización:</span>
        </template>
        <v-radio label="Iframe (roto — ver nota)" value="iframe" density="compact" />
        <v-radio label="Pestaña nueva" value="popup" density="compact" />
      </v-radio-group>
    </div>

    <v-alert v-if="authMode === 'iframe'" type="warning" density="compact" variant="tonal" class="mb-3">
      DigiLogix devuelve <code>500</code> en <code>AutorizarFirmaDocumento</code> cuando su página de
      autorización se embebe en un iframe cross-origin — confirmado de forma aislada, sin depender de
      esta app ni del backend (bug de DigiLogix, no nuestro). Dejamos la opción para referencia, pero usá
      "Pestaña nueva".
    </v-alert>

    <div class="d-flex ga-2 mb-4 flex-wrap">
      <v-btn
        size="small"
        color="secondary"
        :disabled="!hasUsageToken || signLoading"
        :loading="signLoading && signMode === 'legacy'"
        @click="startSign(effectiveHashCount)">
        Firma con DigiLogix
      </v-btn>
      <v-btn
        size="small"
        color="secondary"
        variant="outlined"
        :disabled="!hasUsageToken || signLoading"
        :loading="signLoading && signMode === 'async'"
        @click="startAsyncSign(effectiveHashCount)">
        Firma con DigiLogix asincrónica
      </v-btn>
      <v-btn
        v-if="signResults.length || signError"
        size="small"
        variant="outlined"
        @click="resetSign">
        Limpiar firma
      </v-btn>
    </div>

    <v-alert v-if="signMode === 'async' && asyncStatusText" type="info" density="compact" variant="tonal" class="mb-3">
      {{ asyncStatusText }}
    </v-alert>

    <v-alert v-if="authMode === 'popup' && signCurrentItem" type="info" density="compact" variant="tonal" class="mb-3">
      Esperando autorización en la ventana emergente
      <span v-if="signMode === 'async'">(asincrónico)</span>
      <span v-if="signTotalCount > 1">({{ signCompletedIds.length + 1 }} de {{ signTotalCount }})</span>...
      <v-btn size="x-small" variant="text" class="ml-2" @click="cancelSign">Cancelar</v-btn>
    </v-alert>

    <v-alert v-if="signError" type="error" density="compact" variant="tonal" class="mb-3">{{ signError }}</v-alert>

    <template v-if="signResults.length">
      <v-alert type="success" density="compact" variant="tonal" class="mb-3">
        Firma completada ({{ signResults.length }} hash{{ signResults.length > 1 ? 'es' : '' }}).
      </v-alert>
      <v-table density="compact">
        <thead>
          <tr>
            <th class="text-caption">#</th>
            <th class="text-caption">Request ID</th>
            <th class="text-caption">Hash original</th>
            <th class="text-caption">Hash firmado (base64)</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, i) in signResults" :key="item.requestId">
            <td class="text-caption">{{ i + 1 }}</td>
            <td class="text-caption font-mono" style="word-break:break-all">{{ item.requestId }}</td>
            <td class="text-caption font-mono" style="word-break:break-all;max-width:200px">{{ item.hash }}</td>
            <td class="text-caption font-mono" style="word-break:break-all;max-width:200px">{{ item.signedHash }}</td>
          </tr>
        </tbody>
      </v-table>
    </template>

    <!-- DigiLogix auth iframe dialog -->
    <v-dialog v-model="iframeOpen" max-width="900" persistent>
      <v-card>
        <v-card-title class="d-flex align-center justify-space-between pa-3">
          <span class="text-body-2">
            Autorización DigiLogix
            <span v-if="signMode === 'async'" class="text-caption text-medium-emphasis ml-2">(asincrónico)</span>
            <span v-if="signTotalCount > 1" class="text-caption text-medium-emphasis ml-2">
              ({{ signCompletedIds.length + 1 }} de {{ signTotalCount }})
            </span>
          </span>
          <v-btn icon="mdi-close" size="small" variant="text" @click="cancelSign" />
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-0" style="height:520px">
          <v-progress-linear v-if="iframeLoading" indeterminate color="primary" />
          <iframe
            v-if="signCurrentItem"
            :src="signCurrentItem.urlAutorizacion"
            width="100%"
            height="100%"
            style="border:none;display:block"
            @load="iframeLoading = false" />
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-form>
</template>

<script>
import alisonServer from '@/services/alisonServer.js';

// Kept outside Vue's reactivity on purpose: cross-origin Window objects only allow a
// small whitelist of property reads (close, closed, postMessage, ...). If this were
// Vue-reactive data, Vue's automatic isRef() check on every property read would try to
// read `__v_isRef` off the popup window and the browser throws a SecurityError, which
// aborts the message handler before it can advance the sign flow's state.
let authPopupWindow = null;

export default {
  name: 'DigilogixView',
  props: {
    serverConfig: { type: Object, default: null },
  },
  data: () => ({
    loading: false,
    digilogixEntry: null,
    rawResponse: null,
    notFound: false,
    error: null,
    // sign flow
    signLoading: false,
    signError: null,
    signAuthQueue: [],      // [{requestId, urlAutorizacion, hash}] pending authorization
    signCurrentItem: null,  // item currently shown in iframe
    signCompletedIds: [],   // requestIds that finished callback
    signTotalCount: 0,
    signItemMap: {},        // {requestId: hash} for all items in current batch
    signResults: [],        // [{requestId, hash, signedHash}] final results
    // async/polling flow (ALISRV-490)
    signMode: 'legacy',     // 'legacy' | 'async'
    asyncOperationId: null,
    asyncStatusText: null,
    hashCount: 1,
    // auth UI: iframe dialog vs new tab. 'popup' (new tab) is the default and the only
    // one that actually works — DigiLogix's own AutorizarFirmaDocumento 500s when its
    // authorization page is loaded inside a cross-origin iframe (confirmed bug on their
    // side, reproduced independently of this app). 'iframe' is kept only for reference.
    authMode: 'popup',      // 'iframe' | 'popup'
    authPopupCloseCheck: null,
    // iframe
    iframeOpen: false,
    iframeLoading: false,
  }),
  computed: {
    hasUsageToken() {
      return Boolean(this.serverConfig?.usageAccessToken);
    },
    effectiveHashCount() {
      const n = Math.trunc(Number(this.hashCount));
      return Number.isFinite(n) && n > 0 ? n : 1;
    },
  },
  mounted() {
    window.addEventListener('message', this.onDigilogixMessage);
  },
  beforeUnmount() {
    window.removeEventListener('message', this.onDigilogixMessage);
    if (this.authPopupCloseCheck) clearInterval(this.authPopupCloseCheck);
    authPopupWindow?.close();
  },
  methods: {
    async fetchListInfo() {
      this.loading = true;
      this.digilogixEntry = null;
      this.rawResponse = null;
      this.notFound = false;
      this.error = null;

      alisonServer.changeServerURL(this.serverConfig.serverURL);
      alisonServer.changeTenant(this.serverConfig.tenant);
      alisonServer.usageClient.defaults.headers.common['Authorization'] = `Bearer ${this.serverConfig.usageAccessToken}`;

      try {
        const response = await alisonServer.listInfo();
        const list = Array.isArray(response) ? response : [];
        this.rawResponse = list;
        const entry = list.find(c => c.id === 'digilogix');
        if (entry) {
          this.digilogixEntry = entry;
        } else {
          this.notFound = true;
        }
      } catch (e) {
        this.error = e?.response?.data?.error_description ?? e?.message ?? String(e);
      } finally {
        this.loading = false;
      }
    },
    formatDate(iso) {
      if (!iso) return '-';
      return new Date(iso).toLocaleString('es-AR');
    },
    isExpired(iso) {
      if (!iso) return false;
      return new Date(iso) < new Date();
    },

    async startSign(count) {
      this.signMode = 'legacy';
      this.signLoading = true;
      this.signError = null;
      this.signAuthQueue = [];
      this.signCurrentItem = null;
      this.signCompletedIds = [];
      this.signTotalCount = count;
      this.signItemMap = {};
      this.signResults = [];

      alisonServer.changeServerURL(this.serverConfig.serverURL);
      alisonServer.changeTenant(this.serverConfig.tenant);
      alisonServer.usageClient.defaults.headers.common['Authorization'] = `Bearer ${this.serverConfig.usageAccessToken}`;

      try {
        const hashes = [];
        for (let i = 0; i < count; i++) {
          const randomBytes = crypto.getRandomValues(new Uint8Array(32));
          const hashBuffer = await crypto.subtle.digest('SHA-256', randomBytes);
          hashes.push(btoa(String.fromCharCode(...new Uint8Array(hashBuffer))));
        }

        const result = await alisonServer.createDigilogixSignatureRequest(hashes);
        const items = Array.isArray(result) ? result : [result];
        this.signAuthQueue = items.map((item, i) => ({
          requestId: item.requestId,
          urlAutorizacion: item.urlAutorizacion,
          hash: hashes[i],
        }));
        this.signAuthQueue.forEach(item => { this.signItemMap[item.requestId] = item.hash; });
        this.advanceQueue();
      } catch (e) {
        this.signError = e?.response?.data?.error_description ?? e?.response?.data?.message ?? e?.message ?? String(e);
      } finally {
        this.signLoading = false;
      }
    },

    advanceQueue() {
      if (this.signAuthQueue.length === 0) return;
      this.signCurrentItem = this.signAuthQueue.shift();
      if (this.authMode === 'popup') {
        this.openAuthPopup(this.signCurrentItem.urlAutorizacion);
      } else {
        this.iframeLoading = true;
        this.iframeOpen = true;
      }
    },

    openAuthPopup(url) {
      // No size/feature string on purpose: passing width/height opens a small chrome-less
      // popup window, and DigiLogix's authorization page needs a normal top-level tab (a
      // small popup window previously led to it 404ing to /Firmador instead of completing
      // the flow). Omitting the features string makes the browser open a regular tab.
      const popup = window.open(url, 'digilogix-auth');
      if (!popup) {
        this.signError = 'El navegador bloqueó la ventana emergente. Habilitá los popups para este sitio.';
        return;
      }
      authPopupWindow = popup;
      this.authPopupCloseCheck = setInterval(() => {
        if (popup.closed) {
          clearInterval(this.authPopupCloseCheck);
          this.authPopupCloseCheck = null;
          authPopupWindow = null;
        }
      }, 500);
    },

    // Closes whichever auth UI is currently active (iframe dialog or popup window).
    closeAuthUi() {
      if (this.authPopupCloseCheck) {
        clearInterval(this.authPopupCloseCheck);
        this.authPopupCloseCheck = null;
      }
      authPopupWindow?.close();
      authPopupWindow = null;
      this.iframeOpen = false;
    },

    async onDigilogixMessage(event) {
      const { type, requestId } = event.data || {};
      if (type === 'digilogix-ok' && this.signCurrentItem?.requestId === requestId) {
        this.closeAuthUi();
        await this.$nextTick();
        this.signCompletedIds.push(requestId);

        if (this.signMode === 'async') {
          if (this.signAuthQueue.length > 0) {
            this.advanceQueue();
          } else {
            await this.finishAsyncSign(this.signCompletedIds);
          }
          return;
        }

        if (this.signAuthQueue.length > 0) {
          // Try fetching all IDs now — IdentificadorGrupo may have auto-completed the rest
          const allIds = [...this.signCompletedIds, ...this.signAuthQueue.map(i => i.requestId)];
          try {
            await this.fetchSignedHashesWithIds(allIds);
            // All done — group authorization covered the remaining hashes
            this.signCurrentItem = null;
          } catch {
            // Some still pending, show next iframe
            this.advanceQueue();
          }
        } else {
          await this.fetchSignedHashes();
        }
      } else if (type === 'digilogix-timeout') {
        this.closeAuthUi();
        this.signCurrentItem = null;
        this.signError = 'DigiLogix tardó demasiado en procesar la firma.';
        this.signMode = 'legacy';
      } else if (type === 'digilogix-error') {
        this.closeAuthUi();
        this.signCurrentItem = null;
        this.signError = event.data.message || 'Error en DigiLogix.';
        this.signMode = 'legacy';
      }
    },

    async startAsyncSign(count) {
      this.signMode = 'async';
      this.signLoading = true;
      this.signError = null;
      this.signAuthQueue = [];
      this.signCurrentItem = null;
      this.signCompletedIds = [];
      this.signTotalCount = count;
      this.signItemMap = {};
      this.signResults = [];
      this.asyncOperationId = null;
      this.asyncStatusText = 'Creando operación...';

      alisonServer.changeServerURL(this.serverConfig.serverURL);
      alisonServer.changeTenant(this.serverConfig.tenant);
      alisonServer.usageClient.defaults.headers.common['Authorization'] = `Bearer ${this.serverConfig.usageAccessToken}`;

      try {
        const { operationId } = await alisonServer.createDigilogixOperation();
        this.asyncOperationId = operationId;

        const hashes = [];
        for (let i = 0; i < count; i++) {
          const randomBytes = crypto.getRandomValues(new Uint8Array(32));
          const hashBuffer = await crypto.subtle.digest('SHA-256', randomBytes);
          hashes.push(btoa(String.fromCharCode(...new Uint8Array(hashBuffer))));
        }

        this.asyncStatusText = `Creando ${count} solicitud${count > 1 ? 'es' : ''} de firma en DigiLogix (202 Accepted, en background)...`;
        await alisonServer.createDigilogixSignatureRequestAsync(hashes, operationId);

        const requestResult = await this.pollAsyncOperation(operationId, 'Esperando URL(es) de autorización...');
        const items = Array.isArray(requestResult) ? requestResult : [requestResult];

        this.signAuthQueue = items.map((item, i) => ({
          requestId: item.requestId,
          urlAutorizacion: item.urlAutorizacion,
          hash: hashes[i],
        }));
        this.signAuthQueue.forEach(item => { this.signItemMap[item.requestId] = item.hash; });
        this.asyncStatusText = null;
        this.advanceQueue();
      } catch (e) {
        this.signError = e?.response?.data?.error_description ?? e?.response?.data?.message ?? e?.message ?? String(e);
        this.signMode = 'legacy';
        this.asyncStatusText = null;
      } finally {
        this.signLoading = false;
      }
    },

    // Polls GET /digilogix/operation/{id}/status until COMPLETE (returns state.result) or FAILED (throws).
    async pollAsyncOperation(operationId, statusText, { intervalMs = 1500, maxAttempts = 80 } = {}) {
      this.asyncStatusText = statusText;
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const state = await alisonServer.getDigilogixOperationStatus(operationId);
        if (state.status === 'COMPLETE') return state.result;
        if (state.status === 'FAILED') throw new Error(state.error || 'La operación asincrónica falló.');
        await new Promise(resolve => setTimeout(resolve, intervalMs));
      }
      throw new Error('Tiempo de espera agotado esperando la operación asincrónica.');
    },

    async finishAsyncSign(requestIds) {
      this.signLoading = true;
      this.signCurrentItem = null; // no single item left to wait on — hides the popup "waiting" alert
      try {
        this.asyncStatusText = `Confirmando ${requestIds.length} firma${requestIds.length > 1 ? 's' : ''} (202 Accepted, en background)...`;
        await alisonServer.getDigilogixSignedHashesAsync(requestIds, this.asyncOperationId);

        const result = await this.pollAsyncOperation(this.asyncOperationId, 'Recuperando hash(es) firmado(s)...');
        const list = Array.isArray(result) ? result : [result];
        this.signResults = list.map(r => ({
          requestId: r.requestId,
          hash: this.signItemMap[r.requestId] ?? '(desconocido)',
          signedHash: r.signedHash,
        }));
      } catch (e) {
        this.signError = e?.response?.data?.error_description ?? e?.response?.data?.message ?? e?.message ?? String(e);
      } finally {
        this.signLoading = false;
        this.signMode = 'legacy';
        this.asyncStatusText = null;
      }
    },

    async fetchSignedHashesWithIds(ids) {
      const result = await alisonServer.getDigilogixSignedHashes(ids);
      const list = Array.isArray(result) ? result : [result];
      this.signResults = list.map(r => ({
        requestId: r.requestId,
        hash: this.signItemMap[r.requestId] ?? '(desconocido)',
        signedHash: r.signedHash,
      }));
    },

    // Final fetch after the whole queue is authorized. /digilogix/sign-hashes does a single
    // extra poll server-side and returns 409 if still pending — unlike the mid-queue
    // optimistic check, we've got no next iframe to fall back to here, so retry briefly.
    async fetchSignedHashes() {
      this.signCurrentItem = null; // no single item left to wait on — hides the popup "waiting" alert
      const maxAttempts = 6;
      const intervalMs = 1500;
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
          await this.fetchSignedHashesWithIds(this.signCompletedIds);
          return;
        } catch (e) {
          const isPending = e?.response?.status === 409;
          if (!isPending || attempt === maxAttempts) {
            this.signError = e?.response?.data?.error_description ?? e?.response?.data?.message ?? e?.message ?? String(e);
            return;
          }
          await new Promise(resolve => setTimeout(resolve, intervalMs));
        }
      }
    },

    cancelSign() {
      this.closeAuthUi();
      this.signAuthQueue = [];
      this.signCurrentItem = null;
      this.signError = 'Firma cancelada.';
      this.signMode = 'legacy';
      this.asyncStatusText = null;
    },

    resetSign() {
      this.closeAuthUi();
      this.signError = null;
      this.signAuthQueue = [];
      this.signCurrentItem = null;
      this.signCompletedIds = [];
      this.signTotalCount = 0;
      this.signItemMap = {};
      this.signResults = [];
      this.signMode = 'legacy';
      this.asyncOperationId = null;
      this.asyncStatusText = null;
    },
  },
};
</script>
